import React, { useState, useRef, useEffect, useCallback } from 'react';
import { DirectionsRenderer } from 'react-google-maps';
import { withTheme } from 'styled-components';
import { compose } from 'recompose';
import DriverInfo from 'modules/journey/map/DriverInfo';
import { useSelector, useDispatch } from 'react-redux';
import {
  openDriverInfo,
  closeDriverInfo,
  mapMounted as mapMountedAction,
  mapUpdatedParameters as mapUpdatedParametersAction,
} from 'redux/modules/journey-map/effects';

import {
  getItem,
  getDriverPositions,
  getDriverInfoIsOpened,
  getDriverInfoDriverId,
} from 'redux/modules/journey-map/selectors';

import StopMarkers from './StopMarkers';
import DriverMarkers from './DriverMarkers';
import DriversMap from './DriversMap';

import { getRoute, useStopMarkers, useDriverMarkers, useStopBounds } from './utils';

import { loadingElement } from 'modules/google-maps/elements';

const DRIVER_INFO_WIDTH = 520;

const containerElement = <div style={{ height: `100%`, position: 'relative' }} />;

const mapElement = (
  <div
    style={{
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
    }}
  />
);

const openedMapElement = (
  <div
    style={{
      position: 'absolute',
      top: 0,
      right: 0,
      left: DRIVER_INFO_WIDTH,
      bottom: 0,
    }}
  />
);

const enhance = compose(withTheme);

const JourneyMap = ({ country, theme, vehicleTypeFilter, ...props }) => {
  const {
    base: { colorSchema },
  } = theme;

  const { components: { Tag: { skins = {} } = {} } = {} } = theme;

  const [mapBounds, setMapBounds] = useState(null);
  const [mapMounted, setMapMounted] = useState(null);
  const [directions, setDirections] = useState(null);
  const dispatch = useDispatch();
  const mapRef = useRef(null);

  const item = useSelector(getItem);

  const driverPositions = useSelector(getDriverPositions);
  const isDriverInfoOpened = useSelector(getDriverInfoIsOpened);
  const selectedDriverId = useSelector(getDriverInfoDriverId);

  const stopMarkers = useStopMarkers(item || {}, skins);
  const driverMarkers = useDriverMarkers(driverPositions, selectedDriverId);
  const stopBounds = useStopBounds(stopMarkers);

  const onOpenDriverInfo = useCallback(
    ({ driver_id, location_mode, timestamp }) => {
      dispatch(openDriverInfo({ driver_id, location_mode, timestamp }));
    },
    [dispatch]
  );

  const onCloseDriverInfo = useCallback(() => {
    dispatch(closeDriverInfo());
  }, [dispatch]);

  const handleMapMount = useCallback(() => {
    setMapMounted(true);
  }, [setMapMounted]);

  useEffect(() => {
    if (!item || !mapMounted) {
      return;
    }
    const DirectionsService = new window.google.maps.DirectionsService();

    DirectionsService.route(getRoute(item.stops), (result, status) => {
      if (status !== window.google.maps.DirectionsStatus.OK) {
        console.error(`error fetching directions ${result}`);
        return;
      }

      setDirections(result);
    });
  }, [item, mapMounted]);

  const stopBoundsStr = stopBounds && stopBounds.toString();

  useEffect(() => {
    if (!mapRef || !mapRef.current) {
      return;
    }
    !!stopBounds && mapRef.current.fitBounds(stopBounds);
    setMapBounds(mapRef.current.getBounds());
    //eslint-disable-next-line
  }, [mapRef.current, stopBoundsStr, dispatch]);

  const mapBoundsStr = mapBounds && mapBounds.toString();

  useEffect(() => {
    if (!item) {
      return;
    }

    const { id: journeyId } = item;

    if (!journeyId || !mapBounds) {
      return;
    }

    dispatch(mapMountedAction({ journeyId, bounds: mapBounds, vehicleTypeFilter }));
    //eslint-disable-next-line
  }, [mapBoundsStr, item, dispatch]);

  useEffect(() => {
    if (!item) {
      return;
    }

    const { id: journeyId } = item;

    if (!journeyId || !mapBounds) {
      return;
    }

    dispatch(mapUpdatedParametersAction({ journeyId, bounds: mapBounds, vehicleTypeFilter }));
  }, [vehicleTypeFilter, mapBoundsStr, mapBounds, item, dispatch]);

  const getMapRef = useCallback((ref) => {
    mapRef.current = ref;
  }, []);

  if (!item) {
    return loadingElement;
  }

  return (
    <>
      {isDriverInfoOpened && <DriverInfo width={DRIVER_INFO_WIDTH} onClose={onCloseDriverInfo} />}
      <DriversMap
        mapRef={getMapRef}
        containerElement={containerElement}
        mapElement={isDriverInfoOpened ? openedMapElement : mapElement}
        onMount={handleMapMount}
      >
        <StopMarkers stopMarkers={stopMarkers} />
        <DriverMarkers driverMarkers={driverMarkers} onClick={onOpenDriverInfo} />
        {directions && (
          <DirectionsRenderer
            directions={directions}
            options={{
              suppressMarkers: true,
              suppressInfoWindows: true,
              polylineOptions: {
                clickable: false,
                strokeColor: colorSchema.primary300,
                strokeOpacity: 0.9,
              },
            }}
          />
        )}
      </DriversMap>
    </>
  );
};

export default enhance(JourneyMap);
